import { DeleteIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { ENV, ScoresContext } from '../routes/ScoreListing'

function ResetWeekModal() {
  const context = useContext(ScoresContext)
  const disclosure = useDisclosure()
  const toast = useToast()

  const [confirmationValue, setConfirmationValue] = useState('')
  const [isResetting, setIsResetting] = useState(false)

  if (!context) {
    throw new Error('ScoresContext is not available for ResetWeekModal')
  }

  const { weekDocument, resetWeekData, weekNum } = context

  useEffect(() => {
    if (!disclosure.isOpen) {
      setConfirmationValue('')
    }
  }, [disclosure.isOpen])

  const handleOpen = () => {
    disclosure.onOpen()
  }

  const handleReset = async () => {
    if (confirmationValue.trim() !== 'RESET') {
      toast({
        title: 'Confirmation mismatch',
        description: 'Type RESET in uppercase to confirm.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    setIsResetting(true)
    try {
      await resetWeekData()
      toast({
        title: 'Week reset complete',
        description: `Scores and songs removed for week ${weekNum}.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      disclosure.onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast({
        title: 'Reset failed',
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    } finally {
      setIsResetting(false)
    }
  }

  return (
    <>
      <Tooltip hasArrow label={weekDocument ? 'Reset week data' : 'Waiting for week data'}>
        <IconButton
          aria-label="Reset week"
          icon={<DeleteIcon />}
          variant="ghost"
          color="white"
          borderWidth="1px"
          borderColor="whiteAlpha.500"
          _hover={{ bg: 'whiteAlpha.300' }}
          size="sm"
          ml={1}
          onClick={handleOpen}
          isDisabled={!weekDocument}
        />
      </Tooltip>

      <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset week {weekNum}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  This operation permanently deletes every score in{' '}
                  <strong>{`scores_${ENV}/week${weekNum}/scores`}</strong> and removes the
                  downloaded songs for this week. Ensure no one else is working on this data before
                  proceeding.
                </AlertDescription>
              </Alert>

              <Text fontSize="sm" color="gray.600">
                The Firestore document will be cleared so only the forum URL and the week number
                will remain. All other fields ({' '}
                <em>championshipSongs, numPages, lastScraped, etc.</em> ) will be removed.
              </Text>

              <FormControl>
                <FormLabel>Type RESET to confirm</FormLabel>
                <Input
                  value={confirmationValue}
                  onChange={(event) => setConfirmationValue(event.target.value)}
                  placeholder="RESET"
                  autoComplete="off"
                />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={disclosure.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleReset}
              isLoading={isResetting}
              isDisabled={confirmationValue.trim() !== 'RESET'}
            >
              Delete data
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ResetWeekModal
